import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Bike, Clock, Loader2, MessageSquare, ShoppingBag, Star } from "lucide-react";

const SHOP_FALLBACK = "/image-placeholder.svg";

export default function ShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, count } = useCart();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileTab, setMobileTab] = useState("products");

  useEffect(() => {
    Promise.all([
      api.get(`/shops/${id}`),
      api.get("/products", { params: { shop_id: id } }),
      api.get("/reviews", { params: { shop_id: id } }),
    ])
      .then(([shopResponse, productResponse, reviewResponse]) => {
        setShop(shopResponse.data);
        setProducts(productResponse.data);
        setReviews(reviewResponse.data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!shop) return null;

  return (
    <div data-testid="shop-detail">
      <section className="hidden overflow-hidden rounded-b-[34px] bg-brand text-white shadow-brand md:block md:rounded-[36px] lg:grid lg:grid-cols-[1.15fr_.85fr]">
        <div className="relative min-h-[260px] overflow-hidden sm:min-h-[330px] lg:order-2">
          <img
            src={shop.image_path ? fileUrl(shop.image_path) : SHOP_FALLBACK}
            alt={shop.name}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand/85 via-brand/15 to-brand/25 lg:bg-gradient-to-r lg:from-brand/40 lg:to-transparent" />
          <button
            onClick={() => navigate("/app")}
            data-testid="back-btn"
            className="absolute right-4 top-4 hidden h-11 items-center gap-1.5 rounded-full bg-card/90 px-4 text-xs font-extrabold text-brand shadow-sm backdrop-blur-sm md:flex"
          >
            <ArrowRight className="h-4 w-4" /> رجوع
          </button>
          {!shop.is_open && <span className="absolute left-4 top-4 rounded-full bg-destructive px-3 py-1.5 text-xs font-extrabold text-destructive-foreground">مغلق الآن</span>}
        </div>

        <div className="relative flex flex-col justify-center px-5 pb-7 pt-6 sm:p-8 lg:order-1 lg:p-10 xl:p-12">
          <span className="w-fit rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-extrabold text-white/80">{shop.category}</span>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-4xl xl:text-5xl">{shop.name}</h1>
          <p className="mt-3 max-w-lg text-sm leading-7 text-white/60">
            {shop.description || "متجر محلي موثوق، نختار لك منه الأفضل ونوصله إلى بابك."}
          </p>
          {shop.address && <p className="mt-3 text-sm">العنوان: {shop.address}</p>}
          {shop.phone && <a href={`tel:${shop.phone}`} className="mt-2 text-sm underline">الهاتف: <span dir="ltr">{shop.phone}</span></a>}
          {shop.hours && <p className="mt-2 text-sm text-white/80">ساعات العمل: {shop.hours}</p>}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs font-extrabold">
              <Star className="h-4 w-4 fill-rating text-rating" />
              {shop.rating > 0 ? shop.rating : "جديد"}
              {shop.rating_count > 0 && <span className="font-normal text-white/50">({shop.rating_count})</span>}
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs font-extrabold">
              <Bike className="h-4 w-4 text-primary" /> {fmtPrice(shop.delivery_fee)}
            </span>
            <span className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-extrabold ${shop.is_open ? "bg-success/20 text-success-soft" : "bg-destructive/20 text-destructive-foreground"}`}>
              <Clock className="h-4 w-4" /> {shop.is_open ? "مفتوح الآن" : "مغلق"}
            </span>
          </div>
        </div>
      </section>

      <section className="mt-2 flex items-center gap-3 rounded-2xl bg-card p-3 shadow-card md:hidden">
        <img src={shop.image_path ? fileUrl(shop.image_path) : SHOP_FALLBACK} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[10px] font-bold text-muted-foreground"><span>{shop.category}</span><span>·</span><span className={shop.is_open ? "text-success" : "text-destructive"}>{shop.is_open ? "مفتوح" : "مغلق"}</span></div>
          <h1 className="mt-1 truncate text-base font-extrabold">{shop.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-rating text-rating" />{shop.rating > 0 ? shop.rating : "جديد"}</span>
            <span className="inline-flex items-center gap-1"><Bike className="h-3 w-3" />{fmtPrice(shop.delivery_fee)}</span>
          </div>
        </div>
      </section>

      <Tabs value={mobileTab} onValueChange={setMobileTab} className="mt-4 md:hidden">
        <TabsList className="grid h-11 w-full grid-cols-3 rounded-2xl bg-muted p-1">
          <TabsTrigger value="products" className="rounded-xl text-xs font-extrabold data-[state=active]:bg-card data-[state=active]:text-primary">المنتجات ({products.length})</TabsTrigger>
          <TabsTrigger value="about" className="rounded-xl text-xs font-extrabold data-[state=active]:bg-card data-[state=active]:text-primary">عن المتجر</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-xl text-xs font-extrabold data-[state=active]:bg-card data-[state=active]:text-primary">التقييمات</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-3">
          {products.length === 0 ? (
            <div className="rounded-2xl bg-muted py-12 text-center text-sm font-bold text-muted-foreground">لا توجد منتجات بعد</div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} shop={shop} index={index} onOpen={() => navigate(`/app/product/${product.id}`)} onAdd={() => addItem(shop, product)} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="about" className="mt-3 rounded-2xl bg-card p-4 shadow-card">
          <p className="text-sm leading-6 text-muted-foreground">{shop.description || "متجر محلي موثوق، نختار لك منه الأفضل ونوصله إلى بابك."}</p>
          <div className="mt-4 space-y-3 border-t border-border pt-4 text-sm">
            {shop.address && <p><span className="font-bold">العنوان:</span> {shop.address}</p>}
            {shop.hours && <p><span className="font-bold">ساعات العمل:</span> {shop.hours}</p>}
            {shop.phone && <a href={`tel:${shop.phone}`} className="block font-bold text-primary underline">اتصل بالمتجر · <span dir="ltr">{shop.phone}</span></a>}
          </div>
        </TabsContent>
        <TabsContent value="reviews" className="mt-3">
          {reviews.length === 0 ? (
            <div className="rounded-2xl bg-muted py-10 text-center text-sm font-bold text-muted-foreground">لا توجد تقييمات بعد</div>
          ) : (
            <div className="space-y-2">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-2xl bg-card p-4 shadow-card">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-extrabold">{review.buyer_name}</span>
                    <div className="flex gap-0.5" dir="ltr">{[1, 2, 3, 4, 5].map((number) => <Star key={number} className={`h-3.5 w-3.5 ${number <= review.rating ? "fill-rating text-rating" : "text-brand/10"}`} />)}</div>
                  </div>
                  {review.comment && <p className="mt-2 text-xs leading-5 text-muted-foreground">{review.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <section className="mt-10 hidden md:block lg:mt-14">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary">من قائمة المتجر</span>
            <h2 className="mt-1 text-xl font-extrabold tracking-tight sm:text-2xl">اختر ما يعجبك</h2>
          </div>
          <span className="text-xs font-bold text-muted-foreground">{products.length} منتج</span>
        </div>

        {products.length === 0 ? (
          <div className="rounded-[28px] bg-muted py-16 text-center text-sm font-bold text-muted-foreground">لا توجد منتجات بعد</div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:gap-x-5 md:grid-cols-3 xl:grid-cols-4">
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                shop={shop}
                index={index}
                onOpen={() => navigate(`/app/product/${product.id}`)}
                onAdd={() => addItem(shop, product)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mt-12 hidden md:block lg:mt-16">
        <div className="mb-5 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <h2 className="text-xl font-extrabold tracking-tight sm:text-2xl">قالوا عن المتجر</h2>
        </div>

        {reviews.length === 0 ? (
          <div className="rounded-[28px] bg-muted py-12 text-center text-sm font-bold text-muted-foreground">لا توجد تقييمات بعد</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {reviews.map((review) => (
              <div key={review.id} className="rounded-[24px] bg-card p-5 shadow-card">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-extrabold">{review.buyer_name}</span>
                  <div className="flex gap-0.5" dir="ltr">
                    {[1, 2, 3, 4, 5].map((number) => (
                      <Star key={number} className={`h-3.5 w-3.5 ${number <= review.rating ? "fill-rating text-rating" : "text-brand/10"}`} />
                    ))}
                  </div>
                </div>
                {review.comment && <p className="mt-3 text-sm leading-6 text-muted-foreground">{review.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {count > 0 && (
        <button
          data-testid="go-to-cart-fab"
          onClick={() => navigate("/app/checkout")}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+1rem)] left-4 right-4 z-30 mx-auto flex h-14 max-w-md items-center justify-center gap-2 rounded-full bg-primary font-extrabold text-primary-foreground shadow-cta transition-transform hover:-translate-y-0.5 md:bottom-6"
        >
          <ShoppingBag className="h-[18px] w-[18px]" /> عرض السلة ({count})
        </button>
      )}
    </div>
  );
}
